import qs from 'qs';
import { notification } from 'antd';
import { Dispatch } from '@reduxjs/toolkit';
import ApiService from 'utils/ApiCalls';
import globalSlice from './globalSlice';
import {
  IOwnership,
  IResetPass,
  LanguageList,
  ThemeList
} from '../globalTypes';
import { useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import { IGlobalState } from './types';

const {
  set_theme,
  set_lang,
  set_search_params,
  set_loading,
  setActiveFilters,
  set_superAdmin,
  set_hierarchical_levels_scope,
  set_main_organization,
  set_bru_assigned,
  set_schema_tables
} = globalSlice.actions;

const setTheme = (theme: ThemeList) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_theme(theme));
  } catch (error) {
    console.error(error);
  }
};

const setFilters =
  (filters: Record<string, string[]>) => (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(setActiveFilters(filters));
    } catch (error) {
      console.error(error);
    }
  };

const setLang = (lang: LanguageList) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_lang(lang));
  } catch (error) {
    console.error(error);
  }
};

const setSearchParams = (params: string) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_search_params(params));
  } catch (error) {
    console.error(error);
  }
};

const login = async (email: string, password: string, schemaId: string) => {
  try {
    const response = await ApiService.login({
      email,
      password,
      schema_id: schemaId
    });

    // Store user data in sessionStorage for current session persistence
    sessionStorage.setItem('user', JSON.stringify(response.data));

    return response.data;
  } catch (error) {
    console.error(error);
    notification.error({
      message: 'Login Failed',
      description: 'Invalid credentials or server error',
      duration: 20
    });
    throw error;
  }
};

const requestLoginEmail = async (email: string, password: string) => {
  try {
    const response = await ApiService.requestLoginEmail(email, password);
    notification.success({
      message: 'Email Sent',
      description:
        response.data.message || 'Please check your email for the login link',
      duration: 20
    });
    return response.data;
  } catch (error: unknown) {
    console.error(error);
    const err = error as {
      response?: { data?: { error?: string; message?: string } };
    };
    const errorMessage =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      'Unable to send login email';
    notification.error({
      message: 'Request Failed',
      description: errorMessage,
      duration: 20
    });
    throw error;
  }
};

const verifyLoginToken = async (token: string) => {
  try {
    const response = await ApiService.verifyLoginToken(token);
    const userData = {
      ...response.data.user,
      token: response.data.token
    };
    sessionStorage.setItem('user', JSON.stringify(userData));
    notification.success({
      message: 'Success',
      description: response.data.message || 'Login successful',
      duration: 3
    });
    return userData;
  } catch (error) {
    console.error(error);
    notification.error({
      message: 'Verification Failed',
      description: 'Invalid or expired token',
      duration: 20
    });
    throw error;
  }
};

const forgotPassword = async (email: string) => {
  try {
    const response = await ApiService.forgotPassword(email);
    return response.data;
  } catch (error) {
    console.log('Forgot password request failed:', error);
    throw error;
  }
};

const resetPassword = async (formData: IResetPass) => {
  try {
    const response = await ApiService.resetPassword(formData);

    notification.success({
      message: 'Info',
      description: 'Your password has been reseted successfully.',
      duration: 20
    });
  } catch (error) {
    console.log(error);
  }
};

const fetchOwnersList = async () => {
  try {
    const response = await ApiService.getOwnersList();

    return response.data?.ownership?.map((item: IOwnership) => item.per_email);
  } catch (error) {
    console.error(error);
  }
};

const fetchStatusList = async (lang: LanguageList) => {
  try {
    const response = await ApiService.getStatuses(lang);

    return response.data.task_type;
  } catch (error) {
    console.error(error);
  }
};

const fetchTypesList = async (lang: LanguageList) => {
  try {
    const response = await ApiService.getTypesList(lang);

    return response.data.task_type;
  } catch (error) {
    console.error(error);
  }
};

const logout = async () => {
  try {
    const response = await ApiService.logout();
    return response.data;
  } catch (error) {
    console.error('Logout failed:', error);
    throw error;
  }
};

const superuserLogout = async () => {
  try {
    const response = await ApiService.superuserLogout();
    return response.data;
  } catch (error) {
    console.error('Superuser logout failed:', error);
    throw error;
  }
};

const getUserFunctionRoles = async (active: boolean) => {
  try {
    const response = await ApiService.getUserFunctionRoles(active);
    return response;
  } catch (error: any) {
    notification.error({
      message: 'Info',
      description: error?.message
    });
  }
};

const getActiveUsers = async () => {
  try {
    const response = await ApiService.getActiveUsers();
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

const getActiveInactiveOptions = async () => {
  try {
    const response = await ApiService.getActiveInactiveOptions();
    return response;
  } catch (error: any) {
    notification.error({
      message: 'Info',
      description: error?.message
    });
  }
};

const getUserFunctions = async (user_id: any) => {
  try {
    const response = await ApiService.getUserFunctions(user_id);
    return response;
  } catch (error) {
    console.log(error);
  }
};

const signup = async (data: any) => {
  try {
    const response = await ApiService.signup(data);
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'An unexpected error occurred';

    notification.error({
      message: 'Signup Failed',
      description: errorMessage,
      duration: 10
    });

    throw new Error(errorMessage);
  }
};

const userActivityStatus = async (username: string) => {
  try {
    const response = await ApiService.fetchUserActivityStatus(username);
    return response.data;
  } catch (error) {
    console.log('Activity status request failed:', error);
    throw error;
  }
};

const assignUserFunctions = async (user_id: number, function_ids: any) => {
  try {
    const response = await ApiService.assignUserFunctions(
      user_id,
      function_ids
    );
    notification.success({
      message: 'Info',
      description: response?.data.message
    });
    return response;
  } catch (error: any) {
    console.log(error);
    notification.error({
      message: 'Info',
      description:
        error?.response?.data?.message || 'Failed to assign functions'
    });
  }
};

const removeUserFunctions = async (user_id: number, function_ids: any) => {
  try {
    const response = await ApiService.removeUserFunctions(
      user_id,
      function_ids
    );
    notification.success({
      message: 'Info',
      description: response?.data.message
    });
    return response;
  } catch (error: any) {
    console.log(error);
    notification.error({
      message: 'Error',
      description:
        error?.response?.data?.message || 'Failed to remove functions'
    });
  }
};

const superAdminLogin = async (email: string, password: string) => {
  try {
    const response = await ApiService.superAdminLogin({
      email,
      password
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const setSuperAdmin = (isSuperAdmin: boolean) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_superAdmin(isSuperAdmin));
  } catch (error) {
    console.error(error);
  }
};

const fetchGlobalUsers = async () => {
  try {
    const response = await ApiService.fetchGlobalUsers();
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

const fetchGlobalCompanies = async () => {
  try {
    const response = await ApiService.fetchGlobalCompanies();
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

const signupSuperUser = async (data: any) => {
  try {
    const response = await ApiService.signupSuperUser(data);
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'An unexpected error occurred';

    notification.error({
      message: 'Signup Failed',
      description: errorMessage,
      duration: 10
    });

    throw new Error(errorMessage);
  }
};

const postGlobalCompanies = async (data: any) => {
  try {
    const response = await ApiService.postGlobalCompanies(data);
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'An unexpected error occurred';

    notification.error({
      message: 'Submission Failed',
      description: errorMessage,
      duration: 10
    });

    throw new Error(errorMessage);
  }
};

const softDeleteUserBySuperAdmin = async (email: any, schema_id: any) => {
  try {
    const response = await ApiService.softDeleteUser(email, schema_id);
    notification.success({
      message: 'Info',
      description: response?.data.message
    });
    return response;
  } catch (error: any) {
    notification.error({
      message: 'Info',
      description: error?.message
    });
  }
};

const createClientSchema = async (
  schemaId: string,
  file: File
): Promise<string> => {
  try {
    const { data } = await ApiService.createClientSchema(schemaId, file);
    notification.success({
      message: 'Info',
      description: data.message || 'Schema created successfully.'
    });
    return data.message;
  } catch (err: any) {
    notification.error({
      message: 'SQL Upload Failed',
      description: err
    });

    throw new Error(
      err?.response?.data?.message || err.message || 'Upload failed'
    );
  }
};

const uploadExcelEntityTypeActivity = async (
  schemaId: string,
  file: File
): Promise<string> => {
  try {
    const { data } = await ApiService.uploadExcelEntityTypeActivity(
      schemaId,
      file
    );
    notification.success({
      message: 'Info',
      description: data.message || 'Schema created successfully.'
    });
    return data.message;
  } catch (err: any) {
    notification.error({
      message: 'Excel Upload Failed',
      description: err
    });

    throw new Error(
      err?.response?.data?.message || err.message || 'Upload failed'
    );
  }
};

const uploadExcelAccessControl = async (
  schemaId: string,
  file: File
): Promise<string> => {
  try {
    const { data } = await ApiService.uploadExcelAccessControl(schemaId, file);
    notification.success({
      message: 'Info',
      description: data.message || 'Schema created successfully.'
    });
    return data.message;
  } catch (err: any) {
    notification.error({
      message: 'Excxel Upload Failed',
      description: err
    });

    throw new Error(
      err?.response?.data?.message || err.message || 'Upload failed'
    );
  }
};

const uploadParentData = async (
  schemaId: string,
  file: File
): Promise<string> => {
  try {
    const { data } = await ApiService.uploadParentData(schemaId, file);
    notification.success({
      message: 'Info',
      description: data.message || 'Schema created successfully.'
    });
    return data.message;
  } catch (err: any) {
    notification.error({
      message: 'File Upload Failed',
      description: err
    });

    throw new Error(
      err?.response?.data?.message || err.message || 'Upload failed'
    );
  }
};

export const setHierarchicalLevelsScope =
  (values: any) => (dispatch: Dispatch) => {
    dispatch(set_loading());

    dispatch(set_hierarchical_levels_scope(values));
  };

export const updateHierarchicalLevelsScope =
  (values: Partial<IGlobalState['hierarchicalLevelsScope']>) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const current = getState().global.hierarchicalLevelsScope;

    dispatch(set_loading());

    dispatch(
      set_hierarchical_levels_scope({
        ...current,
        ...values
      })
    );
  };

export const setMainOrganization =
  (mainOrgId: number | null) => (dispatch: Dispatch) => {
    dispatch(set_loading());
    dispatch(set_main_organization(mainOrgId));
  };

export const setBruAssigned =
  (bruAssignedValue: string | null) => (dispatch: Dispatch) => {
    dispatch(set_loading());
    dispatch(set_bru_assigned(bruAssignedValue));
  };

export const fetchSchemaTables = () => async (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    const response = await ApiService.getSchemaTables();
    dispatch(set_schema_tables(response.data.tables));
  } catch (error) {
    console.error('Failed to fetch schema tables:', error);
    dispatch(set_schema_tables([]));
  }
};

export default {
  setTheme,
  setLang,
  setSearchParams,
  login,
  requestLoginEmail,
  verifyLoginToken,
  forgotPassword,
  fetchOwnersList,
  fetchStatusList,
  fetchTypesList,
  resetPassword,
  logout,
  superuserLogout,
  setFilters,
  getUserFunctionRoles,
  getActiveUsers,
  getActiveInactiveOptions,
  getUserFunctions,
  signup,
  userActivityStatus,
  assignUserFunctions,
  removeUserFunctions,
  superAdminLogin,
  setSuperAdmin,
  fetchGlobalUsers,
  fetchGlobalCompanies,
  signupSuperUser,
  postGlobalCompanies,
  softDeleteUserBySuperAdmin,
  createClientSchema,
  uploadExcelEntityTypeActivity,
  uploadExcelAccessControl,
  uploadParentData,
  setHierarchicalLevelsScope,
  updateHierarchicalLevelsScope,
  setMainOrganization,
  setBruAssigned,
  fetchSchemaTables
};
